import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ClassroomDto } from '../api/types.js';

export type SubPageKey =
  | 'home'
  | 'calendar'
  | 'day-of-school'
  | 'number'
  | 'weather'
  | 'feelings'
  | 'question'
  | 'pledges'
  | 'youtube'
  | 'links'
  | 'rewards'
  | 'search';

export interface ActiveClassroomContextValue {
  active: ClassroomDto | null;
  subPage: SubPageKey;
  open: (classroom: ClassroomDto) => void;
  leave: () => void;
  goTo: (page: SubPageKey) => void;
}

const Context = createContext<ActiveClassroomContextValue | null>(null);

export function useActiveClassroom(): ActiveClassroomContextValue {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useActiveClassroom must be used inside <ActiveClassroomProvider>');
  return ctx;
}

/* ---------- sessionStorage persistence ----------
 *
 * Without this, every HMR full-reload during dev kicked Sarah back to
 * the post-login hub and she had to re-activate her classroom + walk
 * back to the page she was on. sessionStorage keeps the active
 * classroom + subPage alive across reloads in the same tab, so a Vite
 * HMR or manual refresh leaves her on the same screen.
 *
 * Cleared when the browser tab/window closes — never persists across
 * sessions (you still get a clean sign-in start each day).
 */

const STORAGE_KEY = 'classy.activeClassroom.v1';

const VALID_SUBPAGES: ReadonlySet<SubPageKey> = new Set<SubPageKey>([
  'home',
  'calendar',
  'day-of-school',
  'number',
  'weather',
  'feelings',
  'question',
  'pledges',
  'youtube',
  'links',
  'search',
]);

interface PersistedState {
  classroom: ClassroomDto | null;
  subPage: SubPageKey;
}

function readPersisted(): PersistedState {
  if (typeof sessionStorage === 'undefined') return { classroom: null, subPage: 'home' };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { classroom: null, subPage: 'home' };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    const classroom =
      parsed && typeof parsed.classroom === 'object' && parsed.classroom
        ? (parsed.classroom as ClassroomDto)
        : null;
    const subPage =
      parsed &&
      typeof parsed.subPage === 'string' &&
      VALID_SUBPAGES.has(parsed.subPage as SubPageKey)
        ? (parsed.subPage as SubPageKey)
        : 'home';
    return { classroom, subPage };
  } catch {
    return { classroom: null, subPage: 'home' };
  }
}

function writePersisted(state: PersistedState): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* swallow — quota or private mode */
  }
}

export function ActiveClassroomProvider({ children }: { children: ReactNode }) {
  // Re-hydrate from session on first mount so HMR / manual refresh
  // doesn't bounce the teacher back to the hub.
  const initial = useMemo(() => readPersisted(), []);
  const [active, setActive] = useState<ClassroomDto | null>(initial.classroom);
  const [subPage, setSubPage] = useState<SubPageKey>(initial.subPage);

  useEffect(() => {
    writePersisted({ classroom: active, subPage });
  }, [active, subPage]);

  const value = useMemo<ActiveClassroomContextValue>(
    () => ({
      active,
      subPage,
      open: (classroom) => {
        setActive(classroom);
        setSubPage('home');
      },
      leave: () => {
        setActive(null);
        setSubPage('home');
      },
      goTo: (page) => setSubPage(page),
    }),
    [active, subPage],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const ActiveClassroomContextProvider = Context.Provider;
